import { PropsWithChildren } from 'react';

import cn from 'classnames';

import type { BaseComponentProps } from '../../../../types/common';

import styles from './styles.module.css';

export type AdornmentPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'bottom'
  | 'center'
  | 'mark';

export interface CardMediaAdornmentProps extends BaseComponentProps {
  /**
   * Position of the adornment within the media area, possible values: `top-left`, `top-right`, `bottom-left`, `bottom-right`, `bottom`, `center`, `mark`
   * note: `mark` is for handling an adornment attached to the top left edge of the card (e.g. a bookmark indicator)
   * @param top-left - Positions content in the top-left corner
   * @param top-right - Positions content in the top-right corner
   * @param bottom-left - Positions content in the bottom-left corner
   * @param bottom-right - Positions content in the bottom-right corner
   * @param bottom - Positions content in the bottom center
   * @param center - Positions content in the center
   * @param mark - Positions content in the top left edge of the card (e.g. a bookmark indicator)
   */
  position: AdornmentPosition;

  /**
   * @variant 'on-hover' - show the adornment on hover
   * @variant 'always' - show the adornment always
   * @variant 'rest-only' - show the adornment only on rest state & hide it on hover or focus
   */
  showType?: 'on-hover' | 'always' | 'rest-only';

  /**
   * Content of the adornment
   */
  children?: PropsWithChildren['children'];

  /**
   * Custom class name for the adornment
   */
  className?: string;
}

const stopPropagation = (e: React.SyntheticEvent) => {
  e.stopPropagation();
};

/**
 * Positions content within specific areas of the Card.Media component
 */
export const CardMediaAdornment: React.FC<CardMediaAdornmentProps> = ({
  children,
  position,
  showType = 'on-hover',
  dataTestId,
  className,
}) => {
  return (
    <div
      className={cn(
        styles['media-adornment'],
        styles[`media-adornment-${position}`],
        styles[`media-adornment-${showType}`],
        className
      )}
      data-slot="adornment"
      data-testid={dataTestId}
      onClick={stopPropagation}
      onKeyDown={stopPropagation}
    >
      {children}
    </div>
  );
};
